import { DatePipe } from '@angular/common';
import {
    NGX_MAT_DATE_FORMATS,
    NgxMatDateAdapter,
    NgxMatDateFormats,
    NgxMatDatetimePicker,
} from '@angular-material-components/datetime-picker';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { AuthService, CanComponentDeactivate } from '@klickdata/core/auth';
import { CustomNgxDatetimeAdapter } from '@klickdata/core/application/src/custom-datetime-adapter';
import { FormHelper } from '@klickdata/core/form';
import { MessageFormErrorComponent, MessageSentComponent, MessageService } from '@klickdata/core/message';
import { UserMessage, UserMessageData, UserService } from '@klickdata/core/user';
import { UsersSelectComponent } from '@klickdata/shared-components/src/selection-handler/users-select/users-select.component';
import { Observable, of, Subject } from 'rxjs';
import { filter, first, map, switchMap, takeUntil } from 'rxjs/operators';

const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
    parse: {
        dateInput: ['YYYY-MM-DD HH:mm', 'YYYY-MM-DD', 'L LT', 'L'],
    },
    display: {
        dateInput: 'YYYY-MM-DD HH:mm',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@Component({
    selector: 'admin-welcome-message',
    templateUrl: './welcome-message.component.html',
    styleUrls: ['./welcome-message.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NgxMatDateAdapter,
            useClass: CustomNgxDatetimeAdapter,
            deps: [MAT_DATE_LOCALE],
        },
        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
    ],
})
export class WelcomeMessageComponent implements OnInit, CanComponentDeactivate, OnDestroy {
    @ViewChild(UsersSelectComponent) userSelectComp: UsersSelectComponent;
    @ViewChild('scheduledSendDatePicker') scheduledSendDatePicker: NgxMatDatetimePicker<Date>;
    public loading = false;
    public welcomeForm: FormGroup;
    @Input() params: any;
    @Input() hideActions: boolean;
    @Output() send: EventEmitter<UserMessageData> = new EventEmitter<UserMessageData>();
    @Output() sent: EventEmitter<{ [key: string]: any }> = new EventEmitter<{ [key: string]: any }>();
    private destroy: Subject<boolean> = new Subject<boolean>();
    public send_email: FormControl = new FormControl('1');
    public minScheduleDate = new Date();

    constructor(
        protected fb: FormBuilder,
        protected userService: UserService,
        protected auth: AuthService,
        protected messageService: MessageService,
        private cdr: ChangeDetectorRef
    ) {
        this.welcomeForm = this.fb.group({
            subject: ['', Validators.required],
            body: ['', Validators.required],
            groups: [],
            customer: [],
            import: [],
            user_activation: [],
            sync_all_users: [],
            users_attach: [[]],
            users_detach: [[]],
            scheduled_send_date: [null],
        });
    }

    ngOnInit() {
        if (this.params) {
            this.welcomeForm.patchValue(this.params);
        }

        this.send_email.valueChanges.pipe(takeUntil(this.destroy)).subscribe((value) => {
            const scheduledSendDateControl = this.welcomeForm.controls['scheduled_send_date'];
            if (value === '2') {
                scheduledSendDateControl.setValidators(Validators.required);
            } else {
                scheduledSendDateControl.reset(null);
                scheduledSendDateControl.clearValidators();
            }
            scheduledSendDateControl.updateValueAndValidity();
            this.cdr.markForCheck();
        });
    }

    public onSendEmailModeChange(value: string): void {
        if (value === '2') {
            setTimeout(() => this.scheduledSendDatePicker?.open());
        }
    }

    public submitWelcomeMessage(standardActivation?: boolean): void {
        if ((this.welcomeForm.invalid && !standardActivation) || this.userSelectComp?.canDeactivate()) {
            FormHelper.markForm(this.welcomeForm);
            this.messageService.openMessage(MessageFormErrorComponent);
            return;
        }
        this.prepareUserMessage(standardActivation)
            .pipe(
                filter(msg => {
                    if (!!this.send.observers.length) {
                        this.send.emit(msg);
                        return false;
                    }
                    this.loading = true;
                    return true;
                }),
                switchMap(msg => this.userService.send(msg))
            )
            .subscribe(result => {
                this.loading = false;
                this.messageService.openMessage(MessageSentComponent);
                this.welcomeForm.reset();
                this.welcomeForm.patchValue({ body: '' });
                this.send_email.reset('1');
                FormHelper.resetForm(this.welcomeForm);
                setTimeout(() => {
                    this.sent.emit({ ...this.params, importing: this.params.importing });
                }, 1000);
            });
    }

    protected prepareUserMessage(standardActivation?: boolean): Observable<UserMessageData> {
        const formValue = standardActivation
            ? this.params
            : { ...this.welcomeForm.value, ...this.params };

        if (this.send_email.value === '2' && formValue.scheduled_send_date) {
            formValue.scheduled_send_date = new DatePipe('en').transform(
                formValue.scheduled_send_date,
                'yyyy-MM-dd HH:mm'
            );
        } else {
            delete formValue.scheduled_send_date;
        }

        const data = new UserMessage(formValue).getData();
        if (data.customer) {
            return of(data);
        } else {
            return this.auth.getCustomer().pipe(
                first(),
                map(customer => {
                    data.customer = customer.id;
                    return data;
                })
            );
        }
    }

    ngOnDestroy() {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }

    canDeactivate(): boolean {
        return this.welcomeForm.pristine;
    }
}
